import { getLocales } from 'assets/locales/Locale';
import { ExpressionTypes } from 'models/enums/Automation';
import EngineType from 'models/enums/EngineType';
import FuelType from 'models/enums/FuelType';
import PricingCalculationArgs from 'models/enums/PricingCalculationArgs';
import {
  AutomationOption,
  AutomationRuleSchema,
  automationValueExpressionTypes,
} from 'module/automation/helpers/AutomationTypes';

type VehicleMakeOption = {
  id?: number;
  makeCode?: string;
};

type VehicleModelOption = {
  id?: number;
  code?: string;
};

type VehicleClassOption = {
  code?: string;
};

type SurchargeOption = {
  subfeeId?: number;
  id?: number;
  name?: string;
};

type PricingAutomationSchemaArgs = {
  vehicleMakes?: VehicleMakeOption[];
  vehicleModels?: VehicleModelOption[];
  vehicleClasses?: VehicleClassOption[];
  subfees?: SurchargeOption[];
  apiCallbacks?: {
    onMakeCodeChange?(value: string): void;
    onModelChange?(value: string): void;
  };
};

const vehicleClassPath = PricingCalculationArgs['pricingCalculationArgs.vehicle.vehicleModel.vehicleClasses'];
const yearPath = PricingCalculationArgs['pricingCalculationArgs.vehicle.year'];
const makeCodePath = PricingCalculationArgs['pricingCalculationArgs.vehicle.makeCode'];
const modelPath = PricingCalculationArgs['pricingCalculationArgs.vehicle.model'];
const engineTypePath = PricingCalculationArgs['pricingCalculationArgs.vehicle.engineType'];
const fuelTypePath = PricingCalculationArgs['pricingCalculationArgs.vehicle.fuelType'];

const selectValue = (value?: string) => value || '';

export function createPricingAutomationSchema({
  vehicleMakes,
  vehicleModels,
  vehicleClasses,
  subfees,
  apiCallbacks,
}: PricingAutomationSchemaArgs): AutomationRuleSchema {
  const { lang, translate } = getLocales();
  const makeOptions: AutomationOption[] = vehicleMakes?.map((it) => ({ id: it.makeCode, title: it.makeCode })) || [];
  const modelOptions: AutomationOption[] = vehicleModels?.map((it) => ({ id: it.code, title: it.code })) || [];
  const classOptions: AutomationOption[] = vehicleClasses?.map((it) => ({ id: it.code, title: it.code })) || [];
  const surchargeOptions: AutomationOption[] =
    subfees?.map((it) => ({ id: it.subfeeId || it.id, title: it.name })) || [];

  return {
    expressionProperties: [vehicleClassPath, yearPath, makeCodePath, modelPath, engineTypePath, fuelTypePath],
    effectProperties: ['productPriceInfo.Subfees'],
    allowMultipleRules: false,
    allowMultipleExpressions: true,
    defaultExpression: {
      propertyPath: yearPath,
      type: ExpressionTypes.Equal,
      value: '',
    },
    defaultEffects: [],
    properties: {
      [vehicleClassPath]: {
        propertyPath: vehicleClassPath,
        label: lang.vehicleClass,
        inputType: 'select',
        supportedExpressions: [ExpressionTypes.Contains],
        options: classOptions,
        containsValuePath: 'Code',
        defaultValue: '',
        formatValue: (value) => classOptions.find((option) => option.id === value)?.title || value?.toString() || '',
      },
      [yearPath]: {
        propertyPath: yearPath,
        label: lang.year,
        inputType: 'number',
        supportedExpressions: [
          ExpressionTypes.Equal,
          ExpressionTypes.GreaterThan,
          ExpressionTypes.GreaterThanOrEqual,
          ExpressionTypes.LessThan,
          ExpressionTypes.LessThanOrEqual,
          ExpressionTypes.Range,
          ExpressionTypes.Any,
          ...automationValueExpressionTypes,
        ],
      },
      [makeCodePath]: {
        propertyPath: makeCodePath,
        label: lang.makeCode,
        inputType: 'select',
        supportedExpressions: [ExpressionTypes.Equal],
        options: makeOptions,
        parseValue: selectValue,
        onValueChange: (value) => apiCallbacks?.onMakeCodeChange?.(value),
      },
      [modelPath]: {
        propertyPath: modelPath,
        label: lang.model,
        inputType: 'select',
        supportedExpressions: [ExpressionTypes.Equal],
        options: modelOptions,
        parseValue: selectValue,
        onValueChange: (value) => apiCallbacks?.onModelChange?.(value),
      },
      [engineTypePath]: {
        propertyPath: engineTypePath,
        label: lang.engineType,
        inputType: 'select',
        supportedExpressions: [ExpressionTypes.Equal],
        options: Object.values(EngineType).map((it) => ({ id: it, title: translate(it) })),
        parseValue: selectValue,
      },
      [fuelTypePath]: {
        propertyPath: fuelTypePath,
        label: lang.fuelType,
        inputType: 'select',
        supportedExpressions: [ExpressionTypes.Equal],
        options: Object.values(FuelType).map((it) => ({ id: it, title: translate(it) })),
        parseValue: selectValue,
      },
    },
    effects: [
      {
        type: 'pathIdMultiSelect',
        propertyPathPrefix: 'productPriceInfo.Subfees',
        label: lang.applySurchargeRuleDescription || lang.surcharges,
        options: surchargeOptions,
        value: 'true',
        buildPropertyPath: (id) => `productPriceInfo.Subfees[Id=${id}].IsMandatory`,
        parseIdFromPropertyPath: (propertyPath) => {
          const match = propertyPath?.match(/productPriceInfo\.Subfees\[Id=(\d+)\]\.IsMandatory/);
          return match ? Number(match[1]) : null;
        },
      },
    ],
  };
}
